import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { GetMedicalCard } from '../../../api/healthcardApi';

// Interfaces
interface MedicalCard {
    insurer_name: string;
    policy_number: string;
    insured_company_name: string;
    insured_person_name: string;
    insured_person_birth_date: string;
    insured_person_pin: string;
    insured_person_mobile_number: string;
    medical_insurance_program: string;
    start_date: string;
    end_date: string;
    status: string;
    call_center_number: string;
    emergency_call_number: string;
    medical_coverages: string[];
    product_document: string;
}

interface HealthInsuranceState {
    medicalCard: MedicalCard | null;
    loading: boolean;
    error: string | null;
    message: string | null;
}

interface MedicalCardResponse {
    data: MedicalCard;
    status: number;
    message?: string;
}

interface ErrorDetail {
    code: string;
    detail: string;
    attr: string | null;
}

interface StructuredErrorResponse {
    type: 'validation_error' | 'server_error';
    errors: ErrorDetail[];
}

interface GenericErrorResponse {
    error?: string;
}

// Initial state
const initialState: HealthInsuranceState = {
    medicalCard: null,
    loading: false,
    error: null,
    message: null
};

// Async thunk
export const fetchMedicalCard = createAsyncThunk<
    MedicalCardResponse,
    void,
    { rejectValue: string }
>(
    'healthInsurance/fetchMedicalCard',
    async (_, thunkAPI) => {
        try {
            const state = thunkAPI.getState() as any;
            const language = state.language?.currentLanguage || 'az';

            const result = await GetMedicalCard(language);

            // Success - status 200
            if (result.status === 200) {
                const successData = result.data as any;
                return {
                    data: successData.data || successData,
                    status: result.status,
                    message: successData.message || 'Uğurlu əməliyyat'
                };
            }

            // Error handling
            const errorData = result.data;

            // Structured error
            if (errorData && typeof errorData === 'object' && 'type' in errorData && 'errors' in errorData) {
                const structuredError = errorData as StructuredErrorResponse;
                const firstError = structuredError.errors[0];
                return thunkAPI.rejectWithValue(
                    firstError?.detail || 'Xəta baş verdi'
                );
            }

            // Generic error
            if (errorData && typeof errorData === 'object' && 'error' in errorData) {
                const genericError = errorData as GenericErrorResponse;
                return thunkAPI.rejectWithValue(
                    genericError.error || 'Xəta baş verdi'
                );
            }

            // Network error
            if (result.status === 0) {
                return thunkAPI.rejectWithValue('Şəbəkə xətası - cavab alınmadı');
            }

            // Default error
            return thunkAPI.rejectWithValue('Gözlənilməz xəta baş verdi');

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Məlumat əldə edilmədi';
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

// Slice with reducers
const healthInsuranceSlice = createSlice({
    name: 'healthInsurance',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.message = null;
        },
        resetHealthInsuranceState: (state) => {
            state.medicalCard = null;
            state.loading = false;
            state.error = null;
            state.message = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMedicalCard.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.message = null;
            })
            .addCase(fetchMedicalCard.fulfilled, (state, action: PayloadAction<MedicalCardResponse>) => {
                state.loading = false;
                state.medicalCard = action.payload.data;
                state.error = null;
                state.message = action.payload.message || null;
            })
            .addCase(fetchMedicalCard.rejected, (state, action) => {
                state.loading = false;
                state.medicalCard = null;
                state.error = action.payload || 'Məlumat yüklənmədi';
                state.message = null;
            });
    }
});

export const { clearError, resetHealthInsuranceState } = healthInsuranceSlice.actions;
export default healthInsuranceSlice.reducer;